import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Loader2, Check, X, ChevronDown, Download, History as HistoryIcon, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Nav } from "@/components/Nav";
import { VerdictPanel } from "@/components/VerdictPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { handleReviewError } from "@/lib/reviewError";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

const TEMPLATE = `entry_no,account,debit,credit,narration
1,Machinery A/c,500000,,Machinery purchased
1,Bank A/c,,500000,Machinery purchased
2,Rent Expense A/c,60000,,Office rent for June
2,Input CGST A/c,5400,,
2,Input SGST A/c,5400,,
2,Bank A/c,,64800,
2,TDS Payable A/c,,6000,
3,Salary A/c,120000,,Salary paid
3,Bank A/c,,100000,
`;

export default function Bulk() {
  const { user, refreshEntitlement } = useAuth();
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [framework, setFramework] = useState("indas");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [runs, setRuns] = useState([]);

  const loadRuns = async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/bulk-runs", { params: { limit: 10, offset: 0 } });
      setRuns(data.items);
    } catch {
      /* not signed in */
    }
  };

  useEffect(() => { loadRuns(); }, [user]);

  const openRun = async (jobId) => {
    try {
      const { data: job } = await api.get(`/analyze/bulk/${jobId}`);
      setData(job);
      setOpen(null);
      toast.success("Past run loaded");
    } catch {
      toast.error("Could not load that run");
    }
  };

  const deleteRun = async (jobId) => {
    try {
      await api.delete(`/bulk-runs/${jobId}`);
      setRuns((r) => r.filter((x) => x.job_id !== jobId));
      setData((d) => (d?.job_id === jobId ? null : d));
      toast.success("Run deleted");
    } catch {
      toast.error("Could not delete that run");
    }
  };

  const downloadTemplate = () => {
    const url = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "daybook-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const poll = async (jobId) => {
    for (let i = 0; i < 200; i++) {
      await new Promise((r) => setTimeout(r, 3000));
      try {
        const { data: job } = await api.get(`/analyze/bulk/${jobId}`);
        setData(job);
        if (job.status === "done") {
          toast.success(`${job.total_entries} entries checked · ${job.ai_reviewed} AI-reviewed`);
          loadRuns();
          return;
        }
      } catch {
        toast.error("Lost track of the review job.");
        return;
      }
    }
  };

  const submit = async () => {
    if (!file) return toast.error("Choose a CSV file first.");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("framework", framework);
    setLoading(true);
    setData(null);
    try {
      const { data: job } = await api.post("/analyze/bulk", fd);
      setData(job);
      toast.success(`${job.total_entries} entries parsed — reviewing…`);
      loadRuns();
      refreshEntitlement();
      await poll(job.job_id);
    } catch (e) {
      handleReviewError(e, navigate);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grain min-h-screen">
      <Nav />
      <main className="mx-auto max-w-[1400px] px-6 py-14 lg:px-12">
        <p className="overline text-slate-500">Day book</p>
        <h1 className="font-display mt-4 text-4xl font-bold tracking-tight sm:text-5xl">Bulk CSV review</h1>
        <p className="mt-6 max-w-xl text-sm leading-relaxed text-slate-600">
          Upload a day book as CSV. Every entry goes through the rule engine; the riskiest ones (up to 12) also get a
          full AI review under your selected framework.
        </p>

        <div className="mt-12 grid gap-10 lg:grid-cols-[1fr_1.4fr]">
          <section className="border border-stone-200 bg-white">
            <div className="border-b border-stone-200 p-6">
              <p className="overline mb-3 text-slate-500">Framework</p>
              <div className="flex border border-stone-300">
                {[["indas", "Ind AS"], ["as", "Indian GAAP (AS)"]].map(([k, label]) => (
                  <button key={k} onClick={() => setFramework(k)} disabled={loading} data-testid={`bulk-framework-${k}`}
                    className={`flex-1 px-4 py-3 text-sm font-medium transition-colors duration-150 disabled:opacity-60 ${framework === k ? "bg-[#0f172a] text-white" : "bg-white text-slate-600 hover:bg-stone-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="p-6">
              <p className="overline mb-3 text-slate-500">CSV or Excel file</p>
              <label className="flex cursor-pointer flex-col items-center justify-center border border-dashed border-stone-300 p-10 text-center transition-colors duration-150 hover:border-[#0f172a]">
                <Upload className="h-5 w-5 text-slate-400" />
                <span className="mt-4 font-mono-data text-xs text-slate-600">{file ? file.name : "click to choose a .csv or .xlsx file"}</span>
                <input type="file" accept=".csv,.xlsx,.xlsm,.xls,text/csv" className="hidden" data-testid="bulk-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </label>
              <button onClick={downloadTemplate} data-testid="download-template-btn"
                className="mt-4 inline-flex items-center gap-1.5 font-mono-data text-xs text-slate-500 underline transition-colors duration-150 hover:text-[#0f172a]">
                <Download className="h-3.5 w-3.5" /> download sample template
              </button>
              <p className="mt-4 font-mono-data text-[11px] leading-relaxed text-slate-400">
                Works with plain CSV (entry_no, account, debit, credit, narration), type + amount shapes, and
                Tally / Zoho exports with title rows and To/By prefixes. Max 25 entries per upload.
              </p>
              <button onClick={submit} disabled={loading} data-testid="bulk-analyze-btn"
                className="mt-8 flex w-full items-center justify-center gap-2 bg-[#0f172a] px-6 py-4 text-sm font-medium text-white transition-colors duration-150 hover:bg-stone-800 disabled:opacity-60">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Reviewing day book…</> : "Review day book"}
              </button>
            </div>

            {user && runs.length > 0 && (
              <div className="border-t border-stone-200 p-6" data-testid="past-runs">
                <p className="overline mb-4 flex items-center gap-2 text-slate-500"><HistoryIcon className="h-3.5 w-3.5" /> Past runs</p>
                <div className="divide-y divide-stone-100 border border-stone-200">
                  {runs.map((r) => (
                    <div key={r.job_id} className="flex items-start justify-between gap-3 p-3">
                      <button onClick={() => openRun(r.job_id)} data-testid={`past-run-${r.job_id}`}
                        className="min-w-0 flex-1 text-left transition-colors duration-150 hover:text-[#0f172a]">
                        <p className="truncate font-mono-data text-xs font-medium">{r.source_name || "day-book"}</p>
                        <p className="mt-1 font-mono-data text-[11px] text-slate-400">
                          {r.total_entries} entries · {r.flagged} flagged · {r.ai_reviewed} reviewed · {r.framework === "indas" ? "Ind AS" : "AS"}
                          {r.status === "running" ? " · running" : ""}
                        </p>
                        <p className="font-mono-data text-[11px] text-slate-400">{new Date(r.created_at).toLocaleString("en-IN")}</p>
                      </button>
                      <button onClick={() => deleteRun(r.job_id)} data-testid={`delete-run-${r.job_id}`}
                        className="p-1.5 text-slate-400 transition-colors duration-150 hover:text-red-700">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          <section>
            {!data ? (
              <div className="flex h-full flex-col items-center justify-center border border-dashed border-stone-300 bg-white p-16 text-center" data-testid="bulk-empty">
                <p className="font-display text-lg font-bold tracking-tight">No day book reviewed yet</p>
                <p className="mt-2 max-w-xs text-sm leading-relaxed text-slate-500">Results appear here entry by entry, with the flagged ones first.</p>
              </div>
            ) : (
              <div data-testid="bulk-results">
                {data.status === "running" && (
                  <p className="mb-4 flex items-center gap-2 border border-stone-200 bg-white p-4 font-mono-data text-xs text-slate-600" data-testid="bulk-progress">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    AI review in progress — {data.ai_reviewed} of {data.ai_target} entries done. Results fill in as they arrive.
                  </p>
                )}
                <div className="grid grid-cols-4 gap-px border border-stone-200 bg-stone-200">
                  {[["Entries", data.total_entries, "bulk-total"], ["Balanced", data.balanced, "bulk-balanced"], ["Flagged", data.flagged, "bulk-flagged"], ["AI reviewed", data.ai_reviewed, "bulk-reviewed"]].map(([label, val, tid]) => (
                    <div key={label} className="bg-white p-5">
                      <p className="overline text-slate-500">{label}</p>
                      <p className="mt-1 font-mono-data text-xl font-semibold" data-testid={tid}>{val}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-6 divide-y divide-stone-200 border border-stone-200 bg-white">
                  {data.entries.map((e, i) => (
                    <div key={i} data-testid={`bulk-entry-${e.entry_no}`}>
                      <button onClick={() => setOpen(open === i ? null : i)} data-testid={`bulk-entry-toggle-${e.entry_no}`}
                        className="flex w-full items-start justify-between gap-4 p-5 text-left transition-colors duration-150 hover:bg-stone-50">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`flex h-4 w-4 shrink-0 items-center justify-center ${(e.analysis ? e.analysis.verdict === "correct" : e.rules_passed) ? "bg-emerald-700" : "bg-red-700"} text-white`}>
                              {(e.analysis ? e.analysis.verdict === "correct" : e.rules_passed) ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            </span>
                            <span className="font-mono-data text-xs font-semibold">Entry {e.entry_no}</span>
                            {e.analysis && (
                              <span className={`overline px-2 py-0.5 ${e.analysis.verdict === "correct" ? "bg-emerald-100 text-emerald-900" : e.analysis.verdict === "incorrect" ? "bg-red-100 text-red-900" : "bg-amber-100 text-amber-900"}`}>
                                {e.analysis.verdict.replace("_", " ")}
                              </span>
                            )}
                          </div>
                          <p className="mt-2 truncate font-mono-data text-xs text-slate-600">
                            {e.lines.map((l) => `${l.type === "debit" ? "Dr" : "Cr"} ${l.account}`).join(" · ")}
                          </p>
                          <p className="mt-1 font-mono-data text-[11px] text-slate-400">
                            Dr {money(e.total_debit)} / Cr {money(e.total_credit)}{e.narration ? ` · ${e.narration}` : ""}
                          </p>
                        </div>
                        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform duration-150 ${open === i ? "rotate-180" : ""}`} />
                      </button>
                      {open === i && (
                        <div className="border-t border-stone-100 bg-stone-50 p-5">
                          {e.analysis ? <VerdictPanel result={e.analysis} /> : (
                            <div>
                              <p className="overline mb-3 text-slate-500">Rule engine checks</p>
                              <ul className="space-y-2">
                                {e.rule_checks.map((c, x) => (
                                  <li key={x} className="font-mono-data text-xs text-slate-600">
                                    {c.passed ? "PASS" : "FAIL"} — {c.label}: {c.detail}
                                  </li>
                                ))}
                              </ul>
                              <p className="mt-4 text-xs text-slate-500">{data.status === "running" ? "Waiting in the AI review queue…" : "Not AI-reviewed in this run (limit reached). Analyze it individually for a full verdict."}</p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
